import React, { useState, useEffect, useRef } from "react";
import { Card, Form, Row, Col } from "react-bootstrap";
import ErrorComponent from "../../../Components/ErrorMessage";
import "./RadioQuestion.css";
import { replacePlaceholder } from "../../../Utils/Utils";

function RadioQuestion({ question, handleChange, formValues, showError, getChoiceOrder,
  setChoiceOrder }) {
  const [sortedChoices, setSortedChoices] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherInputValue, setOtherInputValue] = useState("");

  const seedRef = useRef({});

  const questionKey = question.tempId;
  const questionText = replacePlaceholder(question.questionText, formValues);

  const getCurrentValue = () => {
    const value = formValues[questionKey];
    return Array.isArray(value) ? value[0] || "" : value || "";
  };

  const currentValue = getCurrentValue();
  const hasAnswer = currentValue !== "";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (currentValue && typeof currentValue === "string" && currentValue.startsWith("Other: ")) {
      setShowOtherInput(true);
      setOtherInputValue(currentValue.replace("Other: ", ""));
    } else if (currentValue === "Other") {
      setShowOtherInput(true);
      setOtherInputValue("");
    } else {
      setShowOtherInput(false);
    }
  }, [currentValue]);

useEffect(() => {
    // If parent has frozen order for this question, use it
    const frozen = getChoiceOrder ? getChoiceOrder(questionKey) : undefined;
    const selected = getCurrentValue();

    if (frozen) {
      setSortedChoices(frozen);
      return;
    }

    let choicesToRender = Array.isArray(question.choices) ? [...question.choices] : [];
    const otherOption = choicesToRender.find((c) => c.value === "Other");
    choicesToRender = choicesToRender.filter((c) => c.value !== "Other");

    // Only compute initial ordering if user hasn't selected yet
    if (!selected) {
      if (question.choicesOrder === "random") {
        if (!seedRef.current[questionKey]) {
          seedRef.current[questionKey] = Math.random();
        }
        choicesToRender = shuffleArrayWithSeed(choicesToRender, seedRef.current[questionKey]);
      } else if (question.choicesOrder === "ascending") {
        choicesToRender.sort((a, b) => (a.text || "").localeCompare(b.text || ""));
      } else if (question.choicesOrder === "descending") {
        choicesToRender.sort((a, b) => (b.text || "").localeCompare(a.text || ""));
      }
    }

    if (otherOption) choicesToRender.push(otherOption);

    // If already selected on mount (rare), freeze in parent
    if (selected && setChoiceOrder) {
      setChoiceOrder(questionKey, choicesToRender);
    }

    setSortedChoices(choicesToRender);
    // keep dependency minimal so this runs only on mount for this question
  }, [questionKey]); // intentionally minimal

  const shuffleArrayWithSeed = (array, seed) => {
    const newArray = [...array];
    let random = seed;
    for (let i = newArray.length - 1; i > 0; i--) {
      random = (random * 9301 + 49297) % 233280;
      const j = Math.floor((random / 233280) * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const getNumCols = () => (isMobile ? question.mobileOptions || 1 : question.webOptions || 1);

  const handleOptionsChange = (event) => {
    // freeze order into parent storage
    if (setChoiceOrder) {
      setChoiceOrder(questionKey, sortedChoices);
    }
    handleChange(event);
  };

  const handleOtherInputChange = (event) => {
    const inputValue = event.target.value;
    setOtherInputValue(inputValue);
    const otherWithValue = `Other: ${inputValue}`;
    handleChange({
      target: {
        name: questionKey,
        value: otherWithValue,
        type: "radio",
      },
    });
  };
return (
    <Card className={`mt-3 ${question.isMandatory && showError && !hasAnswer ? "border-danger" : ""}`}>
      <Card.Body>
        <Form.Group>
          <div className="d-flex flex-column">
            <Form.Label>
              <b style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {questionKey}.{questionText}
                {question.isMandatory && <span className="text-danger">*</span>}
              </b>
            </Form.Label>
            {question.questionDescription && (
              <Form.Label>
                <b style={{ color: "lightblue" }}>{question.questionDescription}</b>
              </Form.Label>
            )}
            {question.isMandatory && showError && !hasAnswer && <ErrorComponent message={question.requiredErrorText} />}
          </div>

          <Row className="radio-row">
            {sortedChoices.map((choice, index) => (
              <Col xs={12 / getNumCols()} key={choice.tempId ?? index}>
                <div className="radio-box" onClick={() => {
                  // better: avoid DOM query; trigger change programmatically:
                  // create synthetic event
                  const synthetic = { target: { name: questionKey, value: choice.value, type: "radio" } };
                  handleOptionsChange(synthetic);
                }}>
                  <Form.Check
                    type="radio"
                    label={choice.text}
                    name={questionKey}
                    value={choice.value}
                    onChange={handleOptionsChange}
                    checked={
                      choice.value === currentValue ||
                      (choice.value === "Other" && typeof currentValue === "string" && currentValue.startsWith("Other: "))
                    }
                    required={question.isMandatory}
                  />
                </div>
              </Col>
            ))}
          </Row>

          {showOtherInput && (
            <Row className="mt-3">
              <Col xs={12}>
                <div className="other-input">
                  <Form.Control
                    type="text"
                    placeholder="Type here..."
                    value={otherInputValue}
                    onChange={handleOtherInputChange}
                  />
                </div>
              </Col>
            </Row>
          )}
        </Form.Group>
      </Card.Body>
    </Card>
  );
}


export default RadioQuestion;
